import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import Grid from '@mui/material/Grid2';
import { Box, Button } from '@mui/material';

import { useTranslate } from 'src/locales';
import { AuthLoader } from 'src/pages/auth/components/shared/AuthLoader';

import { Form, Field } from 'src/components/hook-form';

import { SmsField } from '../channels/SMS/components/SMSFields';
import { LineFields } from '../channels/Line/components/LineFields';
import { DidFields } from '../channels/Voice/components/VoiceFields';
import { useGetAssigneeList } from '../../tickets/hooks/useGetAssigneeList';
import { LiveChatFields } from '../channels/Livechat/components/LiveChatFields';
import { TelegramFields } from '../channels/Telegram/components/TelegramFields';
import { WhatsappFields } from '../channels/Whatsapp/components/WhatsappFields';
import { MessengerField } from '../channels/Messenger/components/MessengerFields';
import { InstagramField } from '../channels/Instagram/components/InstagramFields';
import { MultiSelectAutocomplete } from '../../tickets/components/custom-multiSelect';
// import { WhatsappCallingEnable } from '../channels/Whatsapp/components/whatsapp-calling-enable';
import { useChannelValidation, type EditChannelSchemaType } from '../hooks/useChannelValidation';
import {
  GoogleFields,
  MicrosoftFields,
  OtherProviderFields,
} from '../channels/Email/components/EmailFields';

interface ChannelFormProps {
  selectedChannel: any;
  onSubmit: (data: Partial<EditChannelSchemaType>) => void;
  isUpdateChannelLoading: boolean;
  FacebookDetail: any;
  setFacebookDetail: (value: any) => void;
  connectMessenger: () => void;
  connectInstagram: () => void;
}

export const ChannelForm = ({
  selectedChannel,
  onSubmit,
  isUpdateChannelLoading,
  FacebookDetail,
  setFacebookDetail,
  connectMessenger,
  connectInstagram,
}: ChannelFormProps) => {
  const { t } = useTranslate('translation');
  const isFacebookConnected = Boolean((FacebookDetail as { main_token?: string })?.main_token);
  const { validationSchema } = useChannelValidation(selectedChannel, isFacebookConnected);

  const defaultValues: EditChannelSchemaType = {
    channelName: '',
    assignUsers: [],
    email: '',
  };

  const methods = useForm<EditChannelSchemaType>({
    mode: 'all',
    resolver: zodResolver(validationSchema),
    defaultValues,
    values: {
      ...selectedChannel,
      email: selectedChannel?.config?.credentials?.email ?? '',
      pin:
        selectedChannel?.channelType === 'WHATSAPP'
          ? (selectedChannel.config?.settings?.pin ?? '')
          : '',
      pinAcknowledge: false,
      assignUsers: selectedChannel?.channelassign ?? [],
      smtpEnabled: selectedChannel?.config?.settings?.smtpEnabled ?? false,
      smtpAddress:
        selectedChannel?.config?.credentials?.smtpAddress ??
        selectedChannel?.config?.settings?.smtpAddress ??
        '',
      smtpPort:
        selectedChannel?.config?.credentials?.smtpPort ??
        selectedChannel?.config?.settings?.smtpPort ??
        '',
      smtpLogin:
        selectedChannel?.config?.credentials?.smtpLogin ??
        selectedChannel?.config?.settings?.smtpLogin ??
        '',
      smtpPassword:
        selectedChannel?.config?.credentials?.smtpPassword ??
        selectedChannel?.config?.settings?.smtpPassword ??
        '',
      smtpDomain:
        selectedChannel?.config?.credentials?.smtpDomain ??
        selectedChannel?.config?.credentials?.smtpAddress ??
        '',
      imapEnabled: selectedChannel?.config?.settings?.imapEnabled ?? false,
      imapAddress:
        selectedChannel?.config?.credentials?.imapAddress ??
        selectedChannel?.config?.settings?.imapAddress ??
        '',
      imapPort:
        selectedChannel?.config?.credentials?.imapPort ??
        selectedChannel?.config?.settings?.imapPort ??
        '',
      imapLogin:
        selectedChannel?.config?.credentials?.imapLogin ??
        selectedChannel?.config?.settings?.imapLogin ??
        '',
      imapPassword:
        selectedChannel?.config?.credentials?.imapPassword ??
        selectedChannel?.config?.settings?.imapPassword ??
        '',
      imapEnableSsl:
        selectedChannel?.config?.credentials?.imapEnableSsl ??
        selectedChannel?.config?.settings?.imapEnableSsl ??
        false,
      websiteDomin: selectedChannel?.config?.settings?.domainUrl ?? '',
      widgetColor: selectedChannel?.config?.settings?.widgetColor ?? '',
      welcomeTitle: selectedChannel?.config?.settings?.welcomeTitle ?? '',
      welcomeTagline: selectedChannel?.config?.settings?.welcomeTagline ?? '',
      srcNumber: selectedChannel?.config?.settings?.phoneNumber ?? '',
      authToken: selectedChannel?.config?.credentials?.authToken ?? '',
      accountSid:
        selectedChannel?.config?.credentials?.accountSid ??
        selectedChannel?.config?.credentials?.authId ??
        '',
      lineChannelSecret: selectedChannel?.config?.credentials?.lineChannelSecret ?? '',
      lineChannelId: selectedChannel?.config?.credentials?.lineChannelId ?? '',
      telegramBotToken: selectedChannel?.config?.credentials?.botToken ?? '',
      telegramBotUsername: selectedChannel?.config?.credentials?.botUsername ?? '',
    },
  });

  const { handleSubmit, setValue, control, watch } = methods;

  const {
    _recordList,
    searchValue,
    isFetching,
    open: openList,
    listScrollHandler,
    searchValueHandler,
    openListHandler,
    closeListHandler,
  } = useGetAssigneeList({
    selectedTicket: selectedChannel,
    setValue: setValue as any,
    isDidPage: selectedChannel?.channelType === 'VOICE',
  });

  const handleChange = ({ value, fieldName }: { value: string; fieldName: string }) => {};

  return (
    <Box sx={{ height: '90%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {isUpdateChannelLoading && <AuthLoader />}

      <Form
        methods={methods}
        onSubmit={handleSubmit(onSubmit)}
        style={{ height: '100%', width: '100%' }}
      >
        <Grid container spacing={3} justifyContent="space-between" width="90%">
          <Box sx={{ my: 1, width: '100%' }}>
            <Field.Text
              name="channelName"
              label={t('CHANNEL_NAME')}
              InputLabelProps={{ shrink: true }}
              placeholder={t('ENTER_CHANNEL_NAME_PLACEHOLDER')}
            />
          </Box>

          {selectedChannel.channelType === 'LIVE_CHAT' && (
            <LiveChatFields t={t} selectedChannel={selectedChannel} setValue={setValue} />
          )}

          {selectedChannel.channelType === 'SMS' && <SmsField t={t} />}

          {selectedChannel.channelType === 'LINE' && <LineFields t={t} />}

          {selectedChannel.channelType === 'TELEGRAM' && <TelegramFields t={t} />}

          <Box sx={{ my: 1, width: '100%' }}>
            <MultiSelectAutocomplete
              name="assignUsers"
              label={t('ASSIGNED_TO')}
              placeholder={`+ ${t('RECIPIENTS')}`}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              options={_recordList}
              searchValue={searchValue}
              listScrollHandler={listScrollHandler}
              searchValueHandler={searchValueHandler}
              open={openList}
              openListHandler={openListHandler}
              closeListHandler={closeListHandler}
              isFetching={isFetching}
              onChangeField={handleChange}
            />
          </Box>

          {selectedChannel.channelType === 'WHATSAPP' && (
            <>
              <WhatsappFields
                t={t}
                setFacebookDetail={setFacebookDetail}
                FacebookDetail={FacebookDetail}
              />
              {/* {selectedChannel.id && <WhatsappCallingEnable channelId={selectedChannel.id} />} */}
            </>
          )}

          {selectedChannel.channelType === 'MESSENGER' && (
            <MessengerField t={t} connect={connectMessenger} />
          )}

          {selectedChannel.channelType === 'INSTAGRAM' && (
            <InstagramField t={t} connect={connectInstagram} />
          )}

          {selectedChannel.channelType === 'EMAIL' &&
            selectedChannel.config?.settings?.provider === 'google' && (
              <GoogleFields selectedChannel={selectedChannel} t={t} />
            )}

          {selectedChannel.channelType === 'EMAIL' &&
            selectedChannel.config?.settings?.provider === 'microsoft' && (
              <MicrosoftFields selectedChannel={selectedChannel} t={t} />
            )}

          {selectedChannel.channelType === 'EMAIL' &&
            selectedChannel.config?.settings?.provider === 'other' && (
              <OtherProviderFields control={control} t={t} watch={watch} />
            )}

          {selectedChannel.channelType === 'VOICE' && (
            <DidFields selectedChannel={selectedChannel} />
          )}
        </Grid>

        <Box sx={{ flexGrow: 1 }} />
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            justifyContent: 'flex-end',
            alignItems: 'center',
            width: '90%',
          }}
        >
          <Button type="submit" variant="contained" sx={{ ml: 2 }}>
            {t('SAVE')}
          </Button>
        </Box>
      </Form>
    </Box>
  );
};
